/**
 * Shared diagnostics redaction (Task #2644).
 *
 * Used on BOTH the partner-mobile client (before anything enters the on-device
 * ring buffer or a shared bundle) AND the web server (a defense-in-depth scrub
 * before a report is persisted or emailed, so a client-side regression cannot
 * leak secrets). Keep this the single source of truth — extend it here and both
 * sides inherit the coverage.
 *
 * It strips, from arbitrary free text (error messages, stacks, headers):
 *   - `file://` URIs (may reveal on-device media paths),
 *   - `Bearer <token>` authorization values,
 *   - `x-services-secret` / `x-eatzy-token` / `authorization` header forms,
 *   - access/refresh/eatzy/invitation token key-value pairs in query strings,
 *     JSON, or header-ish text,
 *   - bearer invitation values embedded in `/invite/<token>` and
 *     `/api/invites/<token>` route paths.
 *
 * It does NOT attempt to redact caption / post text — that is never written
 * into the buffer in the first place (callers pass only counts/booleans).
 *
 * Always returns a string and never throws.
 */
export function redactSensitive(input: string): string {
  if (!input) return "";
  let out = String(input);
  try {
    // Invitation URLs are bearer credentials, not harmless route labels. This
    // runs before the generic key/value rules because the value is a path
    // segment rather than a named query parameter. Keep the route shape so
    // request breadcrumbs remain useful without exposing the capability.
    out = out.replace(
      /((?:^|\/)(?:(?:api\/)?invites?|api\/me\/invites?)\/)[^/?#\s]+/gi,
      "$1[REDACTED]"
    );

    // file:// URIs — path may reveal the on-device media location.
    out = out.replace(/file:\/\/[^\s"')]+/gi, "file://<redacted>");

    // Bearer tokens in any header-ish / authorization context.
    out = out.replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer <redacted>");

    // Sensitive header names followed by their value, in either
    // "Header: value" or "Header=value" form. Covers x-services-secret,
    // x-eatzy-token, x-partner-user-id, authorization, cookie, set-cookie.
    out = out.replace(
      /\b(x-services-secret|x-eatzy-token|x-partner-user-id|authorization|cookie|set-cookie)\b(\s*[:=]\s*)("?)[^\s"',;]+\3/gi,
      "$1$2<redacted>"
    );

    // Token-like key/value pairs in query strings, JSON, or form bodies:
    //   ?token=… &access_token=… "refresh_token":"…" inviteToken=…
    //   eatzy_token=… secret=…
    out = out.replace(
      /\b(access_token|refresh_token|id_token|eatzy_token|invite(?:d|ation)?[_-]?token|api[_-]?key|secret|password|token)\b(\s*[:=]\s*)("?)[^\s"',&;]+\3/gi,
      "$1$2<redacted>"
    );
  } catch {
    // Redaction must never throw — fall back to the marker on failure.
    return "<redaction-error>";
  }
  return out;
}

/**
 * Keys which must never carry a credential into a telemetry provider. The
 * string sanitizer above protects serialized values; this key check protects
 * structured Bugsnag metadata and analytics payloads before serialization.
 */
const SENSITIVE_TELEMETRY_KEY = /^(?:authorization|cookie|set-cookie|password|secret|api[_-]?key|token|(?:access|refresh|id|eatzy|invite|invitation|invited)[_-]?token)$/i;

/**
 * Redact telemetry metadata without changing its useful shape. This is
 * intentionally cycle-safe because error reporters may receive arbitrary
 * application objects. Strings are passed through `redactSensitive`, while
 * credential-shaped keys are replaced as a whole so an opaque invite token
 * cannot survive under a camelCase key such as `inviteToken`.
 */
export function redactTelemetryValue(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0
): unknown {
  if (typeof value === "string") return redactSensitive(value);
  if (
    value === null ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint" ||
    typeof value === "undefined"
  ) {
    return value;
  }
  if (depth > 8) return "[REDACTED]";

  if (value instanceof Error) {
    const clean = new Error(redactSensitive(value.message));
    clean.name = redactSensitive(value.name);
    if (value.stack) clean.stack = redactSensitive(value.stack);
    return clean;
  }

  if (typeof value !== "object") return redactSensitive(String(value));
  if (seen.has(value)) return "[CIRCULAR]";
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((entry) => redactTelemetryValue(entry, seen, depth + 1));
  }

  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = SENSITIVE_TELEMETRY_KEY.test(key)
      ? "[REDACTED]"
      : redactTelemetryValue(entry, seen, depth + 1);
  }
  return result;
}

/** True when an analytics/telemetry field name itself identifies a secret. */
export function isSensitiveTelemetryKey(key: string): boolean {
  return SENSITIVE_TELEMETRY_KEY.test(key);
}

/**
 * Return only the documented attribution fields after checking their values.
 * Attribution is intentionally a closed contract: unknown fields are dropped
 * instead of allowing a persisted or malicious `inviteToken` property to be
 * serialized into an analytics header.
 */
export function redactAttributionContext(
  context: unknown
): Record<string, string> | null {
  if (!context || typeof context !== "object" || Array.isArray(context)) {
    return null;
  }
  const allowed = new Set([
    "source",
    "channel",
    "short_link",
    "shortcode",
    "post_id",
    "postId",
    "review_id",
    "reviewId",
    "restaurant_id",
    "restaurantId",
    "campaign_id",
    "campaignId",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "session_id",
    "sessionId",
    "ref",
    "placement",
  ]);
  const safe: Record<string, string> = {};
  for (const [key, value] of Object.entries(context)) {
    if (!allowed.has(key) || isSensitiveTelemetryKey(key)) continue;
    if (typeof value !== "string") continue;
    const clean = redactSensitive(value);
    if (clean !== value) continue;
    safe[key] = value;
  }
  return Object.keys(safe).length > 0 ? safe : null;
}
